/**
 * InfinitSales - useAudioRecorder
 *
 * Hook de gravacao de audio via MediaRecorder, no estilo do WhatsApp:
 * inicia a captura do microfone, conta os segundos e devolve um File pronto
 * para upload ao parar. Usado no input do chat (WA Business e WA Pessoal).
 *
 * - start(): pede permissao do microfone e comeca a gravar. Lanca Error com
 *   mensagem amigavel se o navegador nao suportar ou o usuario negar.
 * - stop(): para e resolve com o File gravado (ou null se vazio).
 * - cancel(): descarta a gravacao sem produzir arquivo.
 *
 * O mimeType e escolhido pela compatibilidade do navegador (Chrome ->
 * audio/webm/opus, Safari -> audio/mp4). O backend converte para ogg/opus
 * antes de enviar ao WhatsApp.
 */
import { useState, useRef, useCallback, useEffect } from 'react';

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

const pickMimeType = () => {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
    return '';
  }
  for (const candidate of MIME_CANDIDATES) {
    try {
      if (window.MediaRecorder.isTypeSupported(candidate)) return candidate;
    } catch (_) {
      // isTypeSupported pode lancar em navegadores antigos — ignora
    }
  }
  return '';
};

const extForMime = (mime) => {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
};

export default function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const resolveRef = useRef(null);
  const canceledRef = useRef(false);

  const isSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined';

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const start = useCallback(async () => {
    setError(null);
    if (!isSupported) {
      const msg = 'Gravacao de audio nao suportada neste navegador.';
      setError(msg);
      throw new Error(msg);
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const msg =
        err && err.name === 'NotAllowedError'
          ? 'Permissao de microfone negada. Libere o acesso para gravar audio.'
          : 'Nao foi possivel acessar o microfone.';
      setError(msg);
      throw new Error(msg);
    }

    streamRef.current = stream;
    const mimeType = pickMimeType();
    let recorder;
    try {
      recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream);
    } catch (_) {
      recorder = new window.MediaRecorder(stream);
    }

    chunksRef.current = [];
    canceledRef.current = false;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const baseType = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0];
      const blob = new Blob(chunksRef.current, { type: baseType });
      const wasCanceled = canceledRef.current;

      cleanup();
      setIsRecording(false);
      setSeconds(0);

      const resolve = resolveRef.current;
      resolveRef.current = null;

      if (wasCanceled || !blob.size) {
        resolve && resolve(null);
        return;
      }

      const ext = extForMime(baseType);
      const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: baseType });
      resolve && resolve(file);
    };

    recorder.start();
    recorderRef.current = recorder;
    setIsRecording(true);
    setSeconds(0);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  }, [isSupported, cleanup]);

  const stop = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      resolveRef.current = resolve;
      canceledRef.current = false;
      recorder.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      canceledRef.current = true;
      recorder.stop(); // onstop limpa tudo e resolve null
    } else {
      cleanup();
      setIsRecording(false);
      setSeconds(0);
    }
  }, [cleanup]);

  // Encerra qualquer gravacao em andamento ao desmontar o componente.
  useEffect(() => {
    return () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        canceledRef.current = true;
        try {
          recorder.stop();
        } catch (_) {
          // ignore
        }
      }
      cleanup();
    };
  }, [cleanup]);

  return { isRecording, seconds, error, isSupported, start, stop, cancel };
}
